import { useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, XCircle, ClipboardCheck, RotateCcw, Volume2, Mic, Square, Loader2, Sparkles, Link2, ExternalLink } from "lucide-react";

const ytEmbed = (url) => {
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

export function StudyMaterial({ elements }) {
  const els = (elements || []).filter((el) => (el.details || "").trim() || (el.images || []).length || (el.videos || []).length || (el.reference_links || []).length);
  if (els.length === 0) return null;
  return (
    <div className="space-y-4" data-testid="study-material">
      {els.map((el) => (
        <div key={el.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`study-el-${el.id}`}>
          {el.title && <p className="font-semibold text-slate-900 mb-1.5">{el.title}</p>}
          {el.details && <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{el.details}</p>}
          {(el.images || []).length > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {el.images.map((src, i) => <img key={i} src={src} alt="" className="rounded-lg border border-slate-200 w-full object-cover" data-testid={`study-img-${el.id}-${i}`} />)}
            </div>
          )}
          {(el.videos || []).filter(Boolean).length > 0 && (
            <div className="mt-3 space-y-2">
              {el.videos.filter(Boolean).map((v, i) => {
                const emb = ytEmbed(v);
                return emb
                  ? <div key={i} className="aspect-video rounded-lg overflow-hidden border border-slate-200"><iframe src={emb} title={`video-${i}`} className="w-full h-full" allowFullScreen data-testid={`study-video-${el.id}-${i}`} /></div>
                  : <a key={i} href={v} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-sky-700 hover:underline" data-testid={`study-video-link-${el.id}-${i}`}><Volume2 className="h-3.5 w-3.5" /> {v}</a>;
              })}
            </div>
          )}
          {(el.reference_links || []).filter(Boolean).length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1.5"><Link2 className="h-3.5 w-3.5" /> Reference links</p>
              <ul className="space-y-1">
                {el.reference_links.filter(Boolean).map((l, i) => (
                  <li key={i}>
                    <a href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline break-all" data-testid={`study-reflink-${el.id}-${i}`}>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Runs a lesson's practice questions, grades on submit and records the attempt/revision.
export default function PracticeRunner({ lesson, traineeId = "preview", level = "", onClose, onGraded }) {
  const questions = useMemo(
    () => (lesson.elements || []).flatMap((el) => (el.questions || []).map((q) => ({ ...q, element_title: el.title }))),
    [lesson]
  );
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [voice, setVoice] = useState(false);
  const [busyVoice, setBusyVoice] = useState(null);
  const [recQ, setRecQ] = useState(null);
  const [aiExpl, setAiExpl] = useState({});
  const [aiLoading, setAiLoading] = useState(null);
  const mediaRef = useRef(null);
  const recStartRef = useRef(0);

  const askAi = async (q) => {
    setAiLoading(q.id);
    try {
      const a = answers[q.id];
      const correct_text = q.type === "numeric"
        ? `${q.lower ?? ""}${q.lower != null && q.upper != null ? "–" : ""}${q.upper ?? ""} ${q.unit || ""}`.trim()
        : (q.options || []).filter((o) => o.correct).map((o) => o.text).join("; ");
      const your_text = q.type === "numeric"
        ? String(a ?? "")
        : (q.options || []).filter((o) => (Array.isArray(a) ? a.includes(o.id) : a === o.id)).map((o) => o.text).join("; ");
      const r = await api.post("/theory/explain", {
        prompt: q.prompt, question_id: q.id, lesson_id: lesson.id, type: q.type, correct_text, your_text,
        lesson_title: lesson.title, element_title: q.element_title,
      });
      setAiExpl((e) => ({ ...e, [q.id]: r.data.explanation }));
    } catch { toast.error("AI tutor is busy — please try again"); } finally { setAiLoading(null); }
  };

  const playText = async (text, id) => {
    if (!text) return;
    setBusyVoice(`tts:${id}`);
    try {
      const r = await api.post("/theory/tts", { text });
      const audio = new Audio(`data:audio/mp3;base64,${r.data.audio_base64}`);
      await audio.play();
    } catch { toast.error("Could not play audio"); } finally { setBusyVoice(null); }
  };

  const startRecord = async (q) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "answer.webm");
        fd.append("duration", String(Math.max(0, (Date.now() - recStartRef.current) / 1000)));
        fd.append("question", JSON.stringify({ id: q.id, type: q.type, prompt: q.prompt, options: q.options, lower: q.lower, upper: q.upper, trainee_id: traineeId, lesson_id: lesson.id, lesson_title: lesson.title }));
        setBusyVoice(`stt:${q.id}`);
        try {
          const r = await api.post("/theory/voice-answer", fd);
          if (q.type === "numeric" && r.data.matched != null) setAnswers((a) => ({ ...a, [q.id]: r.data.matched }));
          else if (r.data.matched && r.data.matched !== "none") setAnswers((a) => ({ ...a, [q.id]: r.data.matched }));
          toast.success(`Heard: “${r.data.transcript || "…"}”`);
        } catch { toast.error("Voice grading failed"); } finally { setBusyVoice(null); }
      };
      mediaRef.current = mr;
      mr.start();
      recStartRef.current = Date.now();
      setRecQ(q.id);
    } catch { toast.error("Microphone unavailable — check browser permissions"); }
  };
  const stopRecord = () => { mediaRef.current?.stop(); setRecQ(null); };

  const setMcq = (qid, oid, multi) =>
    setAnswers((a) => {
      if (!multi) return { ...a, [qid]: oid };
      const cur = Array.isArray(a[qid]) ? a[qid] : [];
      return { ...a, [qid]: cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid] };
    });

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/theory/lessons/${lesson.id}/attempt`, { trainee_id: traineeId, level, answers });
      setResult(r.data);
      onGraded?.(r.data);
    } catch { toast.error("Could not grade practice test"); } finally { setSaving(false); }
  };

  const reset = () => { setAnswers({}); setResult(null); setAiExpl({}); };
  const resById = useMemo(() => Object.fromEntries((result?.results || []).map((r) => [r.question_id, r.correct])), [result]);

  if (questions.length === 0 && !(lesson.elements || []).some((el) => (el.details || "").trim() || (el.images || []).length || (el.videos || []).length || (el.reference_links || []).length))
    return <p className="text-sm text-slate-500 py-6 text-center" data-testid="practice-empty">This lesson has no study material or practice questions yet.</p>;

  return (
    <div className="space-y-5" data-testid="practice-runner">
      <StudyMaterial elements={lesson.elements} />

      <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="voice-mode-toggle">
          <Checkbox checked={voice} onCheckedChange={(v) => setVoice(!!v)} /> <Mic className="h-4 w-4 text-lime-600" /> AI voice mode (listen &amp; answer aloud)
        </label>
        {voice && lesson.intro && (
          <Button size="sm" variant="outline" onClick={() => playText(`${lesson.title}. ${lesson.intro}`, "intro")} disabled={busyVoice === "tts:intro"} data-testid="read-intro-btn">
            {busyVoice === "tts:intro" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />} <span className="ml-1.5">Read intro</span>
          </Button>
        )}
      </div>

      {result && (
        <div className={`rounded-xl p-4 flex items-center justify-between ${result.overall_passed ? "bg-lime-50 border border-lime-200" : "bg-amber-50 border border-amber-200"}`} data-testid="practice-result-banner">
          <div className="flex items-center gap-3">
            {result.overall_passed ? <CheckCircle2 className="h-6 w-6 text-lime-600" /> : <XCircle className="h-6 w-6 text-amber-600" />}
            <div>
              <p className="font-bold text-slate-900">{result.overall_passed ? "Passed" : "Needs revision"} — {result.score_pct}%</p>
              <p className="text-xs text-slate-600">{result.passed}/{result.total} correct{result.failed > 0 ? ` · ${result.failed} added to revision` : ""}</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={reset} data-testid="practice-retry"><RotateCcw className="h-4 w-4 mr-1.5" /> Retry</Button>
        </div>
      )}

      {questions.map((q, i) => {
        const graded = result && q.id in resById;
        const ok = resById[q.id];
        return (
          <div key={q.id} className={`rounded-xl border p-4 ${graded ? (ok ? "border-lime-300 bg-lime-50/40" : "border-red-300 bg-red-50/40") : "border-slate-200 bg-white"}`} data-testid={`practice-q-${q.id}`}>
            <div className="flex items-start gap-2">
              <span className="text-xs font-bold text-slate-400 mt-0.5">Q{i + 1}</span>
              <div className="flex-1">
                <p className="font-medium text-slate-800">{q.prompt}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{q.element_title}</p>
              </div>
              {voice && !result && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500" onClick={() => playText(q.prompt, q.id)} disabled={busyVoice === `tts:${q.id}`} title="Read question aloud" data-testid={`read-q-${q.id}`}>
                    {busyVoice === `tts:${q.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
                  </Button>
                  {recQ === q.id ? (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 animate-pulse" onClick={stopRecord} title="Stop recording" data-testid={`stop-q-${q.id}`}><Square className="h-4 w-4" /></Button>
                  ) : (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-lime-600" onClick={() => startRecord(q)} disabled={busyVoice === `stt:${q.id}`} title="Answer by voice" data-testid={`mic-q-${q.id}`}>
                      {busyVoice === `stt:${q.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              )}
              {graded && (ok ? <CheckCircle2 className="h-5 w-5 text-lime-600" /> : <XCircle className="h-5 w-5 text-red-500" />)}
            </div>

            {q.type === "numeric" ? (
              <div className="mt-3 flex items-center gap-2">
                <Input type="number" step="any" disabled={!!result} value={answers[q.id] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  className="max-w-[160px]" placeholder="Your answer" data-testid={`practice-num-${q.id}`} />
                {q.unit && <span className="text-sm text-slate-500">{q.unit}</span>}
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {(q.options || []).map((o) => {
                  const sel = q.multi ? (Array.isArray(answers[q.id]) && answers[q.id].includes(o.id)) : answers[q.id] === o.id;
                  return (
                    <label key={o.id} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer ${sel ? "border-slate-900 bg-slate-50" : "border-slate-200"} ${result && o.correct ? "border-lime-400 bg-lime-50" : ""}`}
                      data-testid={`practice-opt-${q.id}-${o.id}`}>
                      <Checkbox checked={sel} disabled={!!result} onCheckedChange={() => setMcq(q.id, o.id, q.multi)} className="pointer-events-none" />
                      <span className="text-sm text-slate-700">{o.text}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {graded && (
              <div className="mt-3 space-y-2">
                {q.explanation && (
                  <div className={`rounded-lg p-3 text-sm ${ok ? "bg-lime-50 text-lime-900 border border-lime-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`} data-testid={`explanation-${q.id}`}>
                    <span className="font-semibold">Why: </span>{q.explanation}
                  </div>
                )}
                {!ok && (
                  aiExpl[q.id] ? (
                    <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm text-sky-900" data-testid={`ai-explanation-${q.id}`}>
                      <p className="font-semibold flex items-center gap-1.5 mb-1"><Sparkles className="h-4 w-4" /> AI tutor</p>
                      <p className="whitespace-pre-wrap leading-relaxed">{aiExpl[q.id]}</p>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => askAi(q)} disabled={aiLoading === q.id} className="text-sky-700 border-sky-200 hover:bg-sky-50" data-testid={`ask-ai-${q.id}`}>
                      {aiLoading === q.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Ask AI for a more detailed explanation
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex items-center gap-2">
        {!result && questions.length > 0 && <Button onClick={submit} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="practice-submit"><ClipboardCheck className="h-4 w-4 mr-1.5" /> {saving ? "Grading…" : "Submit practice test"}</Button>}
        {onClose && <Button variant="outline" onClick={onClose} data-testid="practice-close">Close</Button>}
      </div>
    </div>
  );
}
